import React, { useState } from "react";
import { ExternalLink, Phone, Loader2, CalendarCheck } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { HoursTable } from "../components/SharedSections";
import { PRICING, CLINIC } from "../lib/data";

const BOOKING_URL = "https://arena-movement-physiotherapy.splose.com/online-booking/7a1c55bf-8c40-40c2-84d4-8f77fc29b4b9";

export default function Book() {
  const [loaded, setLoaded] = useState(false);

  return (
    <>
      <Seo
        title="Book Online"
        description="Book your physiotherapy or remedial massage appointment online at Arena Movement Physiotherapy in Melbourne. Physio consultations currently Mondays."
        path="/book"
      />
      <PageHeader
        eyebrow="Book Online"
        title="Book your appointment"
        subtitle="Choose a service and a time that suits you using our secure online booking below. Physio consultations are currently Mondays only."
      />

      {/* Prominent CTA bar (always available as a fallback) */}
      <section className="pt-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl bg-navy px-7 sm:px-10 py-7 text-white">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 grid place-items-center rounded-2xl bg-white/10 shrink-0">
                  <CalendarCheck className="h-6 w-6" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold">Secure online booking</div>
                  <p className="text-sm text-white/70">Booking not loading? Open it in a new tab.</p>
                </div>
              </div>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="book-open-newtab"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-navy transition-all hover:-translate-y-0.5"
              >
                Open booking page <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Full-width embed */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="relative w-full overflow-hidden rounded-3xl border border-border-light bg-white shadow-sm" data-testid="booking-embed">
              {!loaded && (
                <div className="absolute inset-0 z-10 grid place-items-center bg-white">
                  <div className="flex flex-col items-center gap-3 text-text-muted">
                    <Loader2 className="h-8 w-8 animate-spin text-navy" />
                    <span className="text-sm">Loading secure booking…</span>
                  </div>
                </div>
              )}
              <iframe
                title="Arena Movement Physiotherapy online booking"
                src={BOOKING_URL}
                name="onlineBookingEmbeded"
                scrolling="auto"
                onLoad={() => setLoaded(true)}
                width="100%"
                height="700px"
                loading="lazy"
                frameBorder="0"
                allow="payment; clipboard-write"
                className="w-full block"
                style={{ minHeight: "700px" }}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Helpful info */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-3 gap-6">
          <Reveal>
            <div className="bg-navy rounded-3xl p-7 text-white h-full">
              <h3 className="font-display text-xl font-semibold">Prefer to talk?</h3>
              <p className="mt-2 text-white/75 text-sm">Give us a call and we'll help you book the right appointment.</p>
              <a href={`tel:${CLINIC.phoneIntl}`} className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-navy" data-testid="book-call-button">
                <Phone className="h-4 w-4" /> {CLINIC.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="bg-white rounded-3xl border border-border-light p-7 h-full">
              <h3 className="font-display text-lg font-semibold text-text-dark">Our services</h3>
              <ul className="mt-4 space-y-3">
                {PRICING.map((p) => (
                  <li key={p.id} className="flex justify-between items-center border-b border-border-light pb-3 last:border-0 last:pb-0">
                    <div>
                      <div className="font-medium text-text-dark">{p.name}</div>
                      <div className="text-sm text-text-muted">{p.duration}</div>
                    </div>
                    <span className="font-display font-bold text-navy">{p.price}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <HoursTable />
          </Reveal>
        </div>
      </section>
    </>
  );
}
